import * as Sentry from '@sentry/react';
import { ApiError } from './api';

/**
 * Browser-side Sentry, initialised from runtime config rather than a
 * build-time env var: the bundle ships with zero env vars, so the DSN
 * comes from GET /api/client-config (one source of truth in the API
 * .env, rotation without a rebuild). Trade-off: errors in the first
 * moments before the config fetch resolves are not captured.
 *
 * No-op when the server has no SENTRY_WEB_DSN configured.
 */

let enabled = false;

// Query strings carry PII here (reception name search, email filters)
// and magic-link tokens are long hex path segments; neither may reach
// the SaaS. Mirrors scrubUrl in the API's config/sentry.ts.
function scrubUrl(url: string): string {
  return url.split('?')[0]!.replace(/\/[a-f0-9]{32,}(?=\/|$)/gi, '/<token>');
}

export async function initSentryFromConfig(): Promise<void> {
  try {
    const res = await fetch('/api/client-config');
    if (!res.ok) return;
    const cfg = (await res.json()) as { sentry_dsn: string | null; environment: string };
    if (!cfg.sentry_dsn) return;
    Sentry.init({
      dsn: cfg.sentry_dsn,
      environment: cfg.environment,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      // Default integrations cover window.onerror and unhandled
      // promise rejections, which the app previously never recorded.
      beforeSend(event) {
        if (event.request?.url) event.request.url = scrubUrl(event.request.url);
        return event;
      },
      // Fetch/XHR/navigation breadcrumbs attach full URLs to every
      // event; scrub them the same way.
      beforeBreadcrumb(crumb) {
        if (typeof crumb.data?.url === 'string') {
          crumb.data.url = scrubUrl(crumb.data.url);
        }
        if (typeof crumb.data?.to === 'string') {
          crumb.data.to = scrubUrl(crumb.data.to);
        }
        if (typeof crumb.data?.from === 'string') {
          crumb.data.from = scrubUrl(crumb.data.from);
        }
        return crumb;
      },
    });
    enabled = true;
  } catch {
    // Monitoring must never break the app; offline boot is normal PWA
    // behaviour and simply runs without reporting.
  }
}

/**
 * True for a request that never reached the server: the connection
 * dropped, the browser cancelled it (navigation, iOS suspending a
 * backgrounded tab), or the venue wifi blipped.
 *
 * These arrive as a bare TypeError from fetch itself, before api() can
 * wrap anything into an ApiError, and the message differs per engine:
 * "Load failed" on Safari/iOS, "Failed to fetch" on Chrome, "NetworkError
 * when attempting to fetch resource" on Firefox.
 */
function isNetworkFailure(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (!(err instanceof TypeError)) return false;
  return /load failed|failed to fetch|networkerror|network request failed/i.test(err.message);
}

/**
 * Report an unexpected client error.
 *
 * Two exclusions, both handled UX rather than defects. ApiError means the
 * server answered and said no; the toast covers the operator and the API
 * reports its own 5xx. A network failure means the request never landed,
 * which on a venue full of phones is weather, not a bug, and would bury
 * everything else on a mobilisation day.
 *
 * The trade-off: a total outage (nginx down, bad deploy) also presents as
 * a network failure, and the API's own Sentry is silent in that case, so
 * neither project would alert. Uptime monitoring is the signal for that,
 * not the browser. A breadcrumb is left behind so if a real error follows,
 * the failed requests leading up to it are still visible.
 */
export function reportWebError(err: unknown): void {
  if (!enabled || err instanceof ApiError) return;
  if (isNetworkFailure(err)) {
    Sentry.addBreadcrumb({
      category: 'network',
      level: 'warning',
      message: `request failed: ${(err as Error).message}`,
    });
    return;
  }
  Sentry.captureException(err);
}
